/**
 * @file     RoundupBlock.tsx
 * @purpose  Renders local_roundup and news_digest blocks as attributed outbound cards with publisher, date, and an external-link affordance.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';
import { formatShortDate } from '@/utils/dateFormat';

type RoundupBlockProps = Extract<ContentBlock, { t: 'local_roundup' | 'news_digest' }>;

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
  </svg>
);

const KIND_LABEL: Record<string, { key: string; defaultValue: string }> = {
  local_business: { key: 'blog.blocks.roundup.kind.local', defaultValue: 'Local' },
  news: { key: 'blog.blocks.roundup.kind.news', defaultValue: 'News' },
  gov: { key: 'blog.blocks.roundup.kind.gov', defaultValue: 'Official' },
};

export const RoundupBlock = ({ caption, items }: RoundupBlockProps) => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  if (items.length === 0) return null;

  const formatDate = (iso?: string) => {
    if (!iso) return null;
    return formatShortDate(iso) || null;
  };

  return (
    <Box component="section">
      {caption ? (
        <Typography
          component="h2"
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.bodyLg,
            fontWeight: 600,
            color: theme.semanticColors.textPrimary,
            mb: '16px',
          }}
        >
          {caption}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {items.map((item) => {
          const published = formatDate(item.publishedAt);
          const kind = KIND_LABEL[item.kind];
          return (
            <Box
              key={item.url}
              component="a"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                p: '16px',
                minHeight: 44,
                cursor: 'pointer',
                textDecoration: 'none',
                border: `1px solid ${theme.semanticColors.borderSubtle}`,
                borderRadius: `${theme.layout.radius.lg}px`,
                backgroundColor: theme.semanticColors.surfaceCard,
                transition: 'background-color 180ms ease, border-color 180ms ease',
                '&:hover': {
                  backgroundColor: theme.semanticColors.surfaceElevated,
                  borderColor: theme.semanticColors.borderStrong,
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
                  outlineOffset: '2px',
                },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: theme.typography.bodySm,
                    fontWeight: 600,
                    color: theme.semanticColors.textPrimary,
                    lineHeight: 1.4,
                    mb: '6px',
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: theme.typography.caption,
                    color: theme.semanticColors.textMuted,
                    letterSpacing: '0.02em',
                  }}
                >
                  {[
                    item.publisher ?? (kind ? t(kind.key, { defaultValue: kind.defaultValue }) : null),
                    published,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <Box sx={{ color: theme.semanticColors.textMuted, flexShrink: 0, mt: '2px' }}>
                <ExternalIcon />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
